/**
 * De onde o número veio — o vocabulário, num lugar só.
 *
 * **Atenção a uma armadilha do nome.** Esta API usa a palavra "confiança" para
 * duas coisas de formatos diferentes:
 *
 * - **objeto** `{ nivel: 1|2|3, rotulo, porque }` — acompanha o resultado da
 *   simulação e responde "de onde veio o número principal desta conta".
 *   Quem desenha é o `SeloDeConfianca`.
 * - **string** `"norma" | "referencia" | "mercado" | "estimativa"` — acompanha
 *   cada premissa e cada item do catálogo de substratos, e responde "de onde
 *   veio este dado". É o que este módulo trata.
 *
 * Os dois nomes iguais são defeito do contrato, não escolha de tela: o enum
 * `Confianca` do motor é a string, e o `Confianca` do `esquemas.py` é o
 * objeto. Está registrado como pendência; renomear mexe na API e nas duas
 * telas de uma vez, e não é hora.
 *
 * Por que isto virou módulo em vez de continuar dentro do `VerAConta.jsx`: a
 * lista de substratos passou a mostrar a mesma string, e duas tabelas de
 * tradução para o mesmo enum divergem — basta alguém acrescentar `norma` numa
 * e esquecer a outra. O que precisa concordar entre telas mora fora delas.
 *
 * **A ordem importa e é a do motor**: norma > referência > mercado >
 * estimativa, do mais forte para o mais fraco. `mercado` não é pior que
 * `referencia` por ser menos científico — é pior porque **envelheceu ou vai
 * envelhecer**: preço de energia medido em 2023 não é o de hoje. E
 * `estimativa` é o único que **ninguém mediu**. Fundir os dois num
 * "aproximado" é o que transforma estudo de viabilidade em folheto, e é a
 * razão de os quatro existirem separados.
 */

/** Os quatro, do mais forte para o mais fraco. A ordem é significado. */
export const PROCEDENCIAS = ['norma', 'referencia', 'mercado', 'estimativa'];

/**
 * A palavra que vai na tela, o que ela significa, e o que fazer a respeito.
 *
 * A cor vem das quatro famílias que o `tokens.css` já tem, mapeadas por
 * **força** e não por tema: verde para o mais forte, vermelho para o que
 * ninguém mediu. O vermelho em `estimativa` é de propósito — é o número que
 * vai ser atacado primeiro numa reunião de banco, e quem monta o estudo
 * precisa enxergá-lo antes do gerente. Não quer dizer erro; a palavra ao lado
 * diz o que é.
 */
export const PROCEDENCIA = {
  norma: {
    rotulo: 'norma técnica',
    classe: 'selo--norma',
    curto: 'Norma',
    significa: 'norma técnica ou publicação oficial',
    oQueFazer: '',
  },
  referencia: {
    rotulo: 'referência publicada',
    classe: 'selo--referencia',
    curto: 'Referência',
    significa: 'artigo revisado por pares, nota técnica ou boletim rastreável',
    oQueFazer: '',
  },
  mercado: {
    rotulo: 'valor de mercado',
    classe: 'selo--mercado',
    curto: 'Mercado',
    significa: 'preço ou prática real, com data',
    oQueFazer: 'confira a data: este número envelhece sozinho',
  },
  estimativa: {
    rotulo: 'estimativa',
    classe: 'selo--estimativa',
    curto: 'Estimativa',
    significa: 'ninguém mediu este caso — é conta nossa, fundamentada',
    oQueFazer: 'para levar a banco ou licenciamento, mande o material para análise',
  },
};

/**
 * A procedência de uma string qualquer, sem estourar.
 *
 * Valor desconhecido devolve uma entrada com o rótulo cru e a cor mais fraca.
 * Errar para o lado forte seria pintar de verde um dado que ninguém sabe de
 * onde veio — o erro tem que doer no sentido que não lisonjeia.
 */
export function procedencia(valor) {
  if (!valor) return null;
  const chave = String(valor).toLowerCase();
  return (
    PROCEDENCIA[chave] ?? {
      rotulo: String(valor),
      classe: 'selo--estimativa',
      curto: String(valor),
      significa: 'valor de procedência que esta versão do app não conhece',
      oQueFazer: 'trate como estimativa até conferir na fonte',
    }
  );
}

/** Só o rótulo, para quando a tela quer texto e não selo. */
export function rotuloDaProcedencia(valor) {
  return procedencia(valor)?.rotulo ?? '';
}

/**
 * Ordena do mais forte para o mais fraco. Desconhecido vai para o fim.
 *
 * Serve para a lista de substratos mostrar primeiro o que tem base — e não
 * por ordem alfabética, que colocaria "estimativa" antes de "referencia" e
 * ensinaria o consultor a ler a lista de cima para baixo na ordem errada.
 */
export function forcaDaProcedencia(valor) {
  const i = PROCEDENCIAS.indexOf(String(valor ?? '').toLowerCase());
  return i === -1 ? PROCEDENCIAS.length : i;
}
